import React, { useEffect, useState } from "react";
import { Table, Button, Tag, Space, Modal, Form } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  createDoctor,
  deleteDoctor,
  fetchDoctors,
  fetchDoctorSchedule,
  scheduleAppointment,
  updateDoctor,
  updateSchedule,
  uploadDoctorFile,
} from "../../Redux/Slice/Admin/Doctor";
import { fetchSpecialities } from "../../Redux/Slice/Admin/Speciality";
import { fetchBranches } from "../../Redux/Slice/Admin/Branch";
import { toast } from "react-toastify";

import DoctorFormModal from "./doctorModal/DoctorForm";
import DoctorScheduleModal from "./doctorModal/DoctorSchedule";

const DoctorManagement = () => {
  const dispatch = useDispatch();
  const [doctors, setDoctors] = useState([]);

  const [editingDoctor, setEditingDoctor] = useState(null);
  const [openForm, setOpenForm] = useState(false);

  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [availabilityForm] = Form.useForm();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: branchData } = useSelector((state) => state.branch.fetch);
  const { data: specialityData } = useSelector(
    (state) => state.speciality.fetch
  );
  const { data: doctorData, loading: doctorLoading } = useSelector(
    (state) => state.doctor.fetch
  );
  const { loading: uploadLoading } = useSelector(
    (state) => state.doctor.upload
  );
  const { loading: createLoading } = useSelector(
    (state) => state.doctor.create
  );
  const { loading: updateLoading } = useSelector(
    (state) => state.doctor.update
  );
  const { loading: scheduleLoading } = useSelector(
    (state) => state.doctor.schedule
  );
  const { loading: deleteLoading } = useSelector(
    (state) => state.doctor.delete
  );
  const { loading: updateScheduleLoading } = useSelector(
    (state) => state.doctor.updateSchdule
  );
  const { data: fetchScheduleData, loading: fetchScheduleLoading } =
    useSelector((state) => state.doctor.fetchSchedule);

  useEffect(() => {
    if (doctorData?.status) setDoctors(doctorData.data || []);
  }, [doctorData]);

  useEffect(() => {
    dispatch(fetchDoctors());
    dispatch(fetchSpecialities());
    dispatch(fetchBranches());
  }, [dispatch]);

  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState(null);

  const handleUpload = async (info, fieldName) => {
    const file = info.fileList[0]?.originFileObj || info.fileList[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", fieldName);

      const res = await dispatch(uploadDoctorFile(formData)).unwrap();

      if (res?.status && res?.data?.filePath) {
        toast.success(`${file.name} uploaded successfully`);

        if (fieldName === "profile") {
          setProfileImageUrl(res.data.filePath);
        } else if (fieldName === "signature") {
          setSignatureUrl(res.data.filePath);
        }
      }
    } catch (err) {
      toast.error(err.message || "Upload failed");
    }
  };

  const openCreate = () => {
    setEditingDoctor(null);
    setOpenForm(true);
  };

  const openEdit = (record) => {
    setEditingDoctor(record);
    setOpenForm(true);
  };

  const saveDoctor = async (payload) => {
    if (editingDoctor) {
      await dispatch(updateDoctor(payload)).unwrap();
      toast.success("Doctor updated successfully");
    } else {
      await dispatch(createDoctor(payload)).unwrap();
      toast.success("Doctor created successfully");
    }
    dispatch(fetchDoctors());
    setOpenForm(false);
  };

  // 👇 open delete modal
  const confirmDelete = (record) => {
    console.log(record, "delete");

    setDeleteTarget(record);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const doctorId = deleteTarget.doctorId;
    try {
      await dispatch(deleteDoctor(doctorId)).unwrap();
      toast.success("Doctor deleted successfully");
      dispatch(fetchDoctors());
    } catch (err) {
      toast.error(err?.message || "Failed to delete doctor");
    } finally {
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  const openAvailabilityModal = (doctor) => {
    const doctorId = doctor.doctorId;
    setSelectedDoctor(doctor);

    // Fetch full schedule details from API
    dispatch(fetchDoctorSchedule(doctorId));
    setAvailabilityModalOpen(true);
  };

  const saveAvailability = async () => {
    const editId = fetchScheduleData?.data?.[0]?.id;

    try {
      const values = await availabilityForm.validateFields();

      const payload = {
        doctorId: selectedDoctor.doctorId,
        days: values.days.join(","), // join array -> "Mon,Tue"
        startTime: values.timeFrom.format("HH:mm"),
        endTime: values.timeTo.format("HH:mm"),
        effectiveFrom: values.effectiveFrom.format("YYYY-MM-DD"),
        effectiveTo: values.effectiveTo.format("YYYY-MM-DD"),
      };

      if (editId) {
        // 🔹 Update existing schedule
        await dispatch(updateSchedule(payload)).unwrap();
        toast.success("Doctor schedule updated successfully");
      } else {
        // 🔹 Create new schedule
        await dispatch(scheduleAppointment(payload)).unwrap();
        toast.success("Doctor schedule created successfully");
      }

      // refresh list & close modal
      dispatch(fetchDoctors());
      setAvailabilityModalOpen(false);
    } catch (err) {
      toast.error(err?.message || "Failed to save schedule");
    }
  };

  const columns = [
    {
      title: "S No",
      render: (_, r, i) => i + 1,
    },
    {
      title: "Name",
      render: (_, r) => `${r.firstName ?? ""} ${r.lastName ?? ""}`,
    },
    { title: "Email", dataIndex: "email" },
    { title: "Contact", dataIndex: "contactInfo" },
    {
      title: "Specialties",
      dataIndex: "specialtyIds",
      render: (ids = []) => (
        <Space wrap>
          {ids.map((id) => {
            const spec = specialityData?.data?.find(
              (s) => s.specialityId === id
            );
            return <Tag key={id}>{spec?.specialityName}</Tag>;
          })}
        </Space>
      ),
    },
    {
      title: "Branches",
      dataIndex: "branchIds",
      render: (ids = []) => (
        <Space wrap>
          {ids.map((id) => {
            const br = branchData?.data?.find((b) => b.branchId === id);
            return <Tag key={id}>{br?.branchName}</Tag>;
          })}
        </Space>
      ),
    },
    { title: "Experience", dataIndex: "experience" },
    {
      title: "Actions",
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            icon={<CalendarOutlined />}
            onClick={() => openAvailabilityModal(record)}
          />
          <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Button
            key={record.doctorId}
            danger
            icon={<DeleteOutlined />}
            onClick={() => confirmDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <h2>Doctors List</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Create
        </Button>
      </div>

      <Table
        rowKey="doctorId"
        columns={columns}
        dataSource={doctors}
        pagination={false}
        loading={doctorLoading}
      />

      {/* Create/Edit Doctor */}
      <DoctorFormModal
        open={openForm}
        onCancel={() => setOpenForm(false)}
        onSave={saveDoctor}
        loading={editingDoctor ? updateLoading : createLoading}
        editingDoctor={editingDoctor}
        specialityData={specialityData}
        branchData={branchData}
        uploadLoading={uploadLoading}
        handleUpload={handleUpload}
        profileImageUrl={profileImageUrl}
        signatureUrl={signatureUrl}
        setProfileImageUrl={setProfileImageUrl}
        setSignatureUrl={setSignatureUrl}
      />

      <DoctorScheduleModal
        open={availabilityModalOpen}
        onCancel={() => setAvailabilityModalOpen(false)}
        onSave={saveAvailability}
        loading={scheduleLoading}
        form={availabilityForm}
        doctor={selectedDoctor}
        scheduleData={fetchScheduleData}
        scheduleLoading={fetchScheduleLoading || updateScheduleLoading}
      />

      {/* Custom Delete Modal */}
      <Modal
        open={deleteModalOpen}
        title="Confirm Delete"
        onCancel={() => setDeleteModalOpen(false)}
        onOk={handleDelete}
        confirmLoading={deleteLoading}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        {deleteTarget && (
          <p>
            Are you sure you want to delete Dr.{" "}
            <b>
              {deleteTarget.firstName} {deleteTarget.lastName}
            </b>
            ?
          </p>
        )}
      </Modal>
    </div>
  );
};

export default DoctorManagement;
